import React, { useEffect, useState } from 'react';
import { Form, Row, Col, Input, Button, Table, Drawer, Select, Typography, Modal } from 'antd';
import {
  searchInvoiceTransactionAudit,
  searchOverrideDelivery,
  searchOverrideInvoice,
  searchOverridePayment,
  updateOverrideStatus,
} from 'services/system_setup/overrideRecord';
import { logAuditTrailEvent } from 'services/system_setup/auditTrail';
import { fetchUsersName } from 'services/users/users';
import { message } from 'antd';

const { Option } = Select;
const { Title } = Typography;

// Helper function to format date to MM-DD-YYYY
function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm}-${dd}-${yyyy}`;
}

function normalizeToArray(payload) {
  if (!payload) return [];
  return Array.isArray(payload) ? payload.filter(Boolean) : [payload];
}

function pickFirstValue(record, keys = []) {
  if (!record) return '';

  for (let idx = 0; idx < keys.length; idx += 1) {
    const key = keys[idx];
    const value = record[key];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return value;
    }
  }

  return '';
}

function mergeInvoiceAuditFields(overrideRows = [], invoiceRows = []) {
  const invoiceMap = new Map();

  invoiceRows.forEach((item) => {
    const keySource = item ? (item.invoice_no || item.invoiceNumber || '') : '';
    const key = String(keySource).trim().toUpperCase();
    if (!key) return;
    invoiceMap.set(key, item);
  });

  return overrideRows.map((row) => {
    const keySource = row ? (row.invoice_no || '') : '';
    const key = String(keySource).trim().toUpperCase();
    const invoiceRow = invoiceMap.get(key);

    if (!invoiceRow) return row;

    const lastUpdatedDateFromTxn = pickFirstValue(invoiceRow, [
      'last_updated_date',
      'updated_date',
      'modified_date',
      'last_update_date',
    ]);
    const lastUpdatedByFromTxn = pickFirstValue(invoiceRow, [
      'last_updated_by_username',
      'updated_by_username',
      'last_updated_by',
      'updated_by',
      'modified_by',
    ]);

    return {
      ...row,
      last_updated_date: lastUpdatedDateFromTxn || row.last_updated_date,
      last_updated_by: lastUpdatedByFromTxn || row.last_updated_by,
    };
  });
}

function buildUserNameMap(users = []) {
  return users.reduce((acc, user) => {
    const rawId = pickFirstValue(user, ['user_id', 'id', 'userID']);
    const key = String(rawId || '').trim();
    if (!key) return acc;

    const displayName = pickFirstValue(user, [
      'user_name',
      'username',
      'full_name',
      'name',
    ]) || `${pickFirstValue(user, ['first_name'])} ${pickFirstValue(user, ['last_name'])}`.trim();

    if (displayName) {
      acc[key] = displayName;
    }

    return acc;
  }, {});
}

function resolveUpdatedByDisplay(record = {}, userNameById = {}) {
  const explicitName = pickFirstValue(record, [
    'last_updated_by_username',
    'updated_by_username',
    'last_updated_by_name',
    'updated_by_name',
  ]);
  if (explicitName) return explicitName;

  const rawUpdatedBy = pickFirstValue(record, ['last_updated_by', 'updated_by', 'modified_by']);
  const normalizedKey = String(rawUpdatedBy || '').trim();
  if (!normalizedKey) return '';

  if (userNameById[normalizedKey]) {
    return userNameById[normalizedKey];
  }

  // Never show numeric IDs in UPDATED BY display.
  if (/^\d+$/.test(normalizedKey)) return '';

  return normalizedKey;
}

function addUpdatedByDisplay(rows = [], userNameById = {}) {
  return rows.map((row) => ({
    ...row,
    updated_by_display: resolveUpdatedByDisplay(row, userNameById),
  }));
}

// Invoice columns
const invoiceColumns = [
  {
    title: 'INVOICE NO',
    dataIndex: 'invoice_no',
    key: 'invoice_no',
  },
  {
    title: 'INVOICE AMOUNT',
    dataIndex: 'invoice_amount',
    key: 'invoice_amount',
    render: (value) => (value || 0).toLocaleString(),
  },
  {
    title: 'STATUS',
    dataIndex: 'status_name',
    key: 'status_name',
  },
  {
    title: 'CUSTOMER',
    dataIndex: 'customer_name',
    key: 'customer_name',
  },
  {
    title: 'CREATED DATE',
    dataIndex: 'created_date',
    key: 'created_date',
    render: formatDate,
  },
  {
    title: 'LAST UPDATED',
    dataIndex: 'last_updated_date',
    key: 'last_updated_date',
    render: formatDate,
  },
  {
    title: 'UPDATED BY',
    dataIndex: 'updated_by_display',
    key: 'updated_by_display',
  },
];

// Delivery columns
const deliveryColumns = [
  {
    title: 'DR NO',
    dataIndex: 'dr_no',
    key: 'dr_no',
  },
  {
    title: 'TOTAL AMOUNT',
    dataIndex: 'total_amount',
    key: 'total_amount',
    render: (value) => (value || 0).toLocaleString(),
  },
  {
    title: 'DR DATE',
    dataIndex: 'dr_date',
    key: 'dr_date',
    render: formatDate,
  },
  {
    title: 'STATUS',
    dataIndex: 'status_name',
    key: 'status_name',
  },
  {
    title: 'CUSTOMER',
    dataIndex: 'customer_name',
    key: 'customer_name',
  },
  {
    title: 'CREATED DATE',
    dataIndex: 'created_date',
    key: 'created_date',
    render: formatDate,
  },
  {
    title: 'LAST UPDATED',
    dataIndex: 'last_updated_date',
    key: 'last_updated_date',
    render: formatDate,
  },
  {
    title: 'UPDATED BY',
    dataIndex: 'updated_by_display',
    key: 'updated_by_display',
  },
];

// Payment columns
const paymentColumns = [
  {
    title: 'PAYMENT NO',
    dataIndex: 'or_no',
    key: 'or_no',
  },
  {
    title: 'PAYMENT AMOUNT',
    dataIndex: 'total_payment_amount',
    key: 'total_payment_amount',
    render: (value) => (value || 0).toLocaleString(),
  },
  {
    title: 'PAYMENT DATE',
    dataIndex: 'payment_date',
    key: 'payment_date',
    render: formatDate,
  },
  {
    title: 'STATUS',
    dataIndex: 'status_name',
    key: 'status_name',
  },
  {
    title: 'CUSTOMER',
    dataIndex: 'customer_name',
    key: 'customer_name',
  },
  {
    title: 'CREATED DATE',
    dataIndex: 'created_date',
    key: 'created_date',
    render: formatDate,
  },
  {
    title: 'LAST UPDATED',
    dataIndex: 'last_updated_date',
    key: 'last_updated_date',
    render: formatDate,
  },
  {
    title: 'UPDATED BY',
    dataIndex: 'updated_by_display',
    key: 'updated_by_display',
  },
];

export default function OverrideRecordTab() {
  const [form] = Form.useForm();
  const [searchType, setSearchType] = useState('INVOICE');
  const [searchValue, setSearchValue] = useState('');
  const [tableValues, setTableValues] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [open, setOpen] = useState(false);
  const [statusId, setStatusId] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [userNameById, setUserNameById] = useState({});

  // Get userID from sessionStorage
  const loggedinUser = JSON.parse(sessionStorage.getItem('LOGGEDIN_USER_DATA'));
  var userID = loggedinUser && loggedinUser.userID;
  const fallbackUpdaterName = String(
    pickFirstValue(loggedinUser, ['userName']) ||
    `${pickFirstValue(loggedinUser, ['givenName'])} ${pickFirstValue(loggedinUser, ['lastName'])}`.trim() ||
    '',
  ).trim();

  useEffect(() => {
    let isMounted = true;
    async function loadUsersNameMap() {
      try {
        const users = await fetchUsersName();
        if (isMounted) {
          setUserNameById(buildUserNameMap(normalizeToArray(users)));
        }
      } catch (error) {
        if (isMounted) {
          setUserNameById({});
        }
      }
    }

    loadUsersNameMap();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!tableValues.length) return;
    setTableValues((prevRows) => addUpdatedByDisplay(prevRows, userNameById));
    setSelectedRecord((prevRecord) => {
      if (!prevRecord) return prevRecord;
      return {
        ...prevRecord,
        updated_by_display: resolveUpdatedByDisplay(prevRecord, userNameById),
      };
    });
  }, [tableValues.length, userNameById]);

  const showDrawer = () => { setOpen(true); };

  const onClose = () => { 
    setOpen(false); 
  };

  // Status options for delivery, invoice, and payment
  const deliveryStatusOptions = [
    { label: 'NEW', value: 1 },
    { label: 'POSTED', value: 2 },
    { label: 'CANCELLED', value: 3 },
  ];
  const invoiceStatusOptions = [
    { label: 'NEW', value: 4 },
    { label: 'POSTED', value: 5 },
    { label: 'CANCELLED', value: 6 },
  ];
  const paymentStatusOptions = [
    { label: 'NEW', value: 7 },
    { label: 'POSTED', value: 8 },
    { label: 'CANCELLED', value: 9 },
  ];

  const findStatusLabel = (type, value) => {
    const normalizedType = String(type || '').toUpperCase();
    const numericValue = Number(value);

    const statusOptionsByType = {
      INVOICE: invoiceStatusOptions,
      DELIVERY: deliveryStatusOptions,
      PAYMENT: paymentStatusOptions,
    };

    const options = statusOptionsByType[normalizedType] || [];
    const matched = options.find((item) => Number(item.value) === numericValue);
    return matched ? matched.label : '';
  };

  async function handleSearch() {
    let result = [];
    if (searchType === 'INVOICE') {
      if (!searchValue) return;
      try {
        const [overrideData, invoiceAuditData] = await Promise.all([
          searchOverrideInvoice(searchValue),
          searchInvoiceTransactionAudit(searchValue).catch(() => []),
        ]);
        const normalizedOverride = normalizeToArray(overrideData);
        const normalizedInvoiceAudit = normalizeToArray(invoiceAuditData);
        const mergedRows = mergeInvoiceAuditFields(normalizedOverride, normalizedInvoiceAudit);
        result = addUpdatedByDisplay(mergedRows, userNameById);
      } catch (err) {
        result = [];
      }
    } else if (searchType === 'DELIVERY') {
      if (!searchValue) return;
      try {
        const data = await searchOverrideDelivery(searchValue);
        result = addUpdatedByDisplay(normalizeToArray(data), userNameById);
      } catch (err) {
        result = [];
      }
    } else if (searchType === 'PAYMENT') {
      if (!searchValue) return;
      try {
        const data = await searchOverridePayment(searchValue);
        result = addUpdatedByDisplay(normalizeToArray(data), userNameById);
      } catch (err) {
        result = [];
      }
    }
    setTableValues(result);

    // Show message if no results found
    if (result.length === 0) {
      message.warning('No result found');
    }
  }

  function updateDrawer(e) {
    setSelectedRecord(e);
    setStatusId(String(e.status_id)); // set statusId from record as string
    showDrawer();
  }

  const handleOverrideSubmit = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmUpdate = async () => {
    if (!selectedRecord) return;
    let type, id, payload;
    let moduleCode = 'sys';
    let transactionType = '';
    let referenceNo = '';
    const updaterPayload = {
      updated_by: userID,
      last_updated_by: userID,
      updated_by_username: fallbackUpdaterName,
      last_updated_by_username: fallbackUpdaterName,
      updated_by_name: fallbackUpdaterName,
      last_updated_by_name: fallbackUpdaterName,
    };
    if (searchType === 'INVOICE') {
      type = 'invoice';
      id = selectedRecord.invoice_no;
      payload = { type, status_id: Number(statusId), invoice_no: id, ...updaterPayload };
      moduleCode = 'inv';
      transactionType = 'INVOICE';
      referenceNo = id;
    } else if (searchType === 'DELIVERY') {
      type = 'delivery';
      id = selectedRecord.dr_no;
      payload = { type, status_id: Number(statusId), dr_no: id, ...updaterPayload };
      moduleCode = 'dr';
      transactionType = 'DELIVERY';
      referenceNo = id;
    } else if (searchType === 'PAYMENT') {
      type = 'payment';
      id = selectedRecord.or_no;
      payload = { type, status_id: Number(statusId), or_no: id, ...updaterPayload };
      moduleCode = 'pymt';
      transactionType = 'PAYMENT';
      referenceNo = id;
    }

    const previousStatus = String((selectedRecord && selectedRecord.status_name) || '').toUpperCase();
    const newStatus = findStatusLabel(searchType, statusId);
    const auditPayload = {
      transaction_type: transactionType,
      transaction_no: referenceNo,
      previous_status: previousStatus,
      target_status: newStatus,
      updated_by: userID,
      source: 'SYSTEM_OVERRIDE',
    };

    setConfirmLoading(true);
    try {
      await updateOverrideStatus(payload);
      void logAuditTrailEvent({
        moduleCode,
        subModuleCode: 'override',
        action: 'OVERRIDE',
        description: `Override ${transactionType} ${referenceNo}: ${previousStatus || 'UNKNOWN'} -> ${newStatus || 'UNKNOWN'}`,
        payload: auditPayload,
        isSuccess: 1,
      });
      message.success('Status updated successfully!');
      setOpen(false);
      setSelectedRecord(null);
      setShowConfirmModal(false);
      setConfirmLoading(false);
      handleSearch();
    } catch (err) {
      void logAuditTrailEvent({
        moduleCode,
        subModuleCode: 'override',
        action: 'OVERRIDE',
        description: `Override ${transactionType} ${referenceNo} failed`,
        payload: {
          ...auditPayload,
          error: err && err.message,
        },
        isSuccess: 0,
      });
      message.error('Failed to update status.');
      setConfirmLoading(false);
    }
  };

  return (
    <div>
      <Title level={4}>System Override</Title>
      <Form
        form={form}
        layout="inline"
        onFinish={handleSearch}
        style={{ marginBottom: 16 }}
      >
        <Row style={{ width: '100%' }} gutter={8} align="middle">
          <Col>
            <Form.Item name="searchType" initialValue={searchType} style={{ marginRight: 8 }}>
              <Select
                style={{ width: 120 }}
                onChange={val => {
                  setSearchType(String(val));
                  setTableValues([]);
                  setSearchValue('');
                  form.resetFields(['searchValue']);
                }}
              >
                <Option value="INVOICE">Invoice No</Option>
                <Option value="DELIVERY">DR No</Option>
                <Option value="PAYMENT">Payment No</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col>
            <Form.Item name="searchValue">
              <Input
                placeholder={
                  searchType === 'INVOICE' ? 'Enter Invoice No' :
                  searchType === 'DELIVERY' ? 'Enter DR No' :
                  'Enter Payment No'
                }
                value={searchValue}
                onChange={e => setSearchValue(e.target.value)}
                style={{ width: 200 }}
              />
            </Form.Item>
          </Col>
          <Col>
            <Button type="primary" htmlType="submit" disabled={!searchValue.trim()}>
              Search
            </Button>
          </Col>
          <Col flex="auto" style={{ textAlign: 'right' }}>
            <Button type="primary" onClick={() => {
              setSearchValue('');
              setTableValues([]);
              form.resetFields(['searchValue']);
            }}>
              Clear
            </Button>
          </Col>
        </Row>
      </Form>
      <Table
        style={{ textTransform: 'uppercase' }}
        columns={
          searchType === 'INVOICE' ? invoiceColumns :
          searchType === 'DELIVERY' ? deliveryColumns :
          paymentColumns
        }
        dataSource={tableValues}
        bordered
        pagination={{ defaultPageSize: 5 }}
        onRow={(record) => {
          return {
            onDoubleClick: (e) => {updateDrawer(record)}
          };
        }}
        rowKey="key"
      />
      <Drawer 
        title={"OVERRIDE " + searchType} 
        placement="right" 
        onClose={onClose}
        width="30%"
        visible={open}
      >
        {selectedRecord && (
          <Form layout="vertical">
            {searchType === 'INVOICE' ? (
              <>
                <Form.Item label="INVOICE NO">
                  <Input value={selectedRecord.invoice_no} disabled />
                </Form.Item>
                <Form.Item label="INVOICE AMOUNT">
                  <Input value={`₱${(selectedRecord.invoice_amount || 0).toLocaleString()}`} disabled />
                </Form.Item>
                <Form.Item label="STATUS">
                  <Select value={statusId} onChange={val => setStatusId(val)} style={{ width: '100%' }}>
                    {invoiceStatusOptions.map(opt => (
                      <Option key={opt.value} value={String(opt.value)}>{opt.label}</Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item label="CUSTOMER">
                  <Input value={selectedRecord.customer_name} disabled />
                </Form.Item>
                <Form.Item label="CREATED DATE">
                  <Input value={selectedRecord.created_date} disabled />
                </Form.Item>
                <Form.Item label="LAST UPDATED">
                  <Input value={selectedRecord.last_updated_date} disabled />
                </Form.Item>
                <Form.Item label="UPDATED BY">
                  <Input value={selectedRecord.updated_by_display} disabled />
                </Form.Item>
              </>
            ) : searchType === 'DELIVERY' ? (
              <>
                <Form.Item label="DR NO">
                  <Input value={selectedRecord.dr_no} disabled />
                </Form.Item>
                <Form.Item label="TOTAL AMOUNT">
                  <Input value={`₱${(selectedRecord.total_amount || 0).toLocaleString()}`} disabled />
                </Form.Item>
                <Form.Item label="STATUS">
                  <Select value={statusId} onChange={val => setStatusId(val)} style={{ width: '100%' }}>
                    {deliveryStatusOptions.map(opt => (
                      <Option key={opt.value} value={String(opt.value)}>{opt.label}</Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item label="DR DATE">
                  <Input value={selectedRecord.dr_date} disabled />
                </Form.Item>
                <Form.Item label="CUSTOMER">
                  <Input value={selectedRecord.customer_name} disabled />
                </Form.Item>
                <Form.Item label="CREATED DATE">
                  <Input value={selectedRecord.created_date} disabled />
                </Form.Item>
                <Form.Item label="LAST UPDATED">
                  <Input value={selectedRecord.last_updated_date} disabled />
                </Form.Item>
                <Form.Item label="UPDATED BY">
                  <Input value={selectedRecord.updated_by_display} disabled />
                </Form.Item>
              </>
            ) : (
              <>
                <Form.Item label="PAYMENT NO">
                  <Input value={selectedRecord.or_no} disabled />
                </Form.Item>
                <Form.Item label="PAYMENT AMOUNT">
                  <Input value={`₱${(selectedRecord.total_payment_amount || 0).toLocaleString()}`} disabled />
                </Form.Item>
                <Form.Item label="STATUS">
                  <Select value={statusId} onChange={val => setStatusId(val)} style={{ width: '100%' }}>
                    {paymentStatusOptions.map(opt => (
                      <Option key={opt.value} value={String(opt.value)}>{opt.label}</Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item label="PAYMENT DATE">
                  <Input value={selectedRecord.payment_date} disabled />
                </Form.Item>
                <Form.Item label="CUSTOMER">
                  <Input value={selectedRecord.customer_name} disabled />
                </Form.Item>
                <Form.Item label="CREATED DATE">
                  <Input value={selectedRecord.created_date} disabled />
                </Form.Item>
                <Form.Item label="LAST UPDATED">
                  <Input value={selectedRecord.last_updated_date} disabled />
                </Form.Item>
                <Form.Item label="UPDATED BY">
                  <Input value={selectedRecord.updated_by_display} disabled />
                </Form.Item>
              </>
            )}
            <div style={{ marginTop: 20, textAlign: 'right' }}>
              <Button style={{ marginRight: 8 }} onClick={onClose}>
                BACK
              </Button>
              <Button type="primary" onClick={handleOverrideSubmit} icon={<i className="anticon anticon-save" />}>
                SAVE
              </Button>
            </div>
          </Form>
        )}
        <Modal
          title="Confirm Update"
          visible={showConfirmModal}
          onOk={handleConfirmUpdate}
          confirmLoading={confirmLoading}
          onCancel={() => setShowConfirmModal(false)}
          okText="Yes, Update"
          cancelText="Cancel"
        >
          <p>Are you sure you want to update the status of this record?</p>
        </Modal>
      </Drawer>
    </div>
  );
} 
